import { Link } from 'react-router-dom';
import type { CommsSentRow } from '@ucp/web/lib';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Skeleton } from '../admin/Skeleton';
import { DirectoryList, type DirectoryRowContent } from '../ui/DirectoryList';
import type { SentActivityStatus } from '../../hooks/useSentActivity';
import { smsDeliverySentence } from './smsDeliveryText';

/**
 * Reverse-chronological "Sent" table (PRO-0620). Renders the counts-only
 * `CommsSentRow[]` as an accessible single-select list (`DirectoryList`): each row
 * shows sent time · subject · method badge(s) with TEXT labels (never colour
 * alone, NF-10) · audience descriptor · volume · status (In progress / Complete
 * with sent/failed). Activating a row opens that send's counts (`onOpenRow` →
 * `GET /sends/:sendId/result`, AC11). No recipient identity or body (SEC-8).
 */

interface SentMessagesTableProps {
  readonly rows: readonly CommsSentRow[];
  readonly status: SentActivityStatus;
  readonly selectedSendId: string | null;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly onOpenRow: (sendId: string) => void;
  readonly onLoadMore: () => void;
  readonly onRetry: () => void;
}

const METHOD_LABEL: Record<'email' | 'sms', string> = { email: 'Email', sms: 'SMS' };

function formatSentAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusText(row: CommsSentRow): string {
  if (row.state === 'COMPLETE') {
    return row.counts.failed > 0
      ? `Complete — ${row.counts.sent} sent, ${row.counts.failed} failed`
      : `Complete — ${row.counts.sent} sent`;
  }
  return `In progress — ${row.counts.inProgress} pending`;
}

function renderRow(row: CommsSentRow): DirectoryRowContent {
  const volumeLabel = `${row.volume} recipient${row.volume === 1 ? '' : 's'}`;
  const routeLabel =
    row.route === 'L2_APPROVAL' ? 'L2 approval' : row.route === 'QUEUE' ? 'Queue' : null;
  const status = statusText(row);
  const delivery = row.smsDelivery ? smsDeliverySentence(row.smsDelivery) : null;
  return {
    title: row.surveyLabel ?? row.subject ?? 'Untitled send',
    meta: (
      <>
        {formatSentAt(row.sentAt)} · {row.audienceDescriptor} · {volumeLabel}
        {routeLabel ? ` · ${routeLabel}` : ''}
        {delivery ? <span className="block">{delivery}</span> : null}
      </>
    ),
    trailing: (
      <span className="flex flex-wrap items-center gap-1">
        {row.methods.map((method) => (
          <Badge key={method} state="upcoming">
            {METHOD_LABEL[method]}
          </Badge>
        ))}
        <Badge state={row.state === 'IN_PROGRESS' ? 'current' : 'done'}>{status}</Badge>
      </span>
    ),
    accessibleSuffix: `${row.methods.map((m) => METHOD_LABEL[m]).join(' and ') || 'No channel'}. ${status}.${delivery ? ` ${delivery}.` : ''}`,
  };
}

export function SentMessagesTable({
  rows,
  status,
  selectedSendId,
  hasMore,
  loadingMore,
  onOpenRow,
  onLoadMore,
  onRetry,
}: SentMessagesTableProps) {
  if (status === 'loading') {
    return <Skeleton rows={5} label="Loading sent messages" />;
  }

  if (status === 'error') {
    return (
      <Card className="max-w-narrow p-6">
        <p role="alert" className="m-0 text-base text-text-secondary">
          Could not load recent sends just now.
        </p>
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-3">
          Try again
        </Button>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card className="max-w-narrow p-6">
        <p className="m-0 text-base text-text-secondary">You haven't sent anything yet.</p>
        <Link
          to="/compose"
          className="mt-3 inline-flex min-h-11 items-center rounded-md bg-primary px-4 py-2 text-base font-semibold text-primary-foreground no-underline transition-colors ease-ds hover:bg-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          Compose a message
        </Link>
      </Card>
    );
  }

  return (
    <div>
      <DirectoryList
        ariaLabelledBy="sent-table-heading"
        items={rows}
        getKey={(row) => row.sendId}
        selectedId={selectedSendId}
        onSelect={onOpenRow}
        renderRow={renderRow}
      />
      {hasMore ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="inline-flex min-h-[44px] items-center rounded-md border border-border bg-surface px-4 text-sm font-medium text-text-primary transition-colors ease-ds hover:bg-muted disabled:opacity-60"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
